<script lang="ts">
  import { tweened } from "svelte/motion";
  import { cubicOut } from "svelte/easing";

  export let progress: number = 0;
  export let buttonClicked: boolean;

  const segmentColors = [
    "hsla(210, 54%, 56%, 1)",
    "hsla(64, 62%, 44%, 1)",
    "hsla(46, 100%, 52%, 1)",
    "hsla(19, 88%, 54%, 1)",
    "hsla(354, 91%, 42%, 1)",
  ];
  const animatedProgress = tweened(0, {
    duration: 800,
    easing: cubicOut,
  });

  $: animatedProgress.set(progress);

  const radius = 50;
  const circumference = 2 * Math.PI * radius;

  let dashArrays = Array(segmentColors.length).fill(`0 ${circumference}`);

  // Reactive block to calculate dash arrays for each segment
  $: {
    dashArrays = segmentColors.map((_, index) => {
      const segmentStart = index * 20;
      const segmentEnd = segmentStart + 20;

      if ($animatedProgress < segmentStart) {
        return `0 ${circumference}`; // No fill if progress hasn't reached this segment
      }

      if ($animatedProgress >= segmentEnd) {
        return `${circumference / 5} ${circumference - circumference / 5}`;
      }

      const segmentProgress = $animatedProgress - segmentStart;
      const filled = (segmentProgress / 20) * (circumference / 5);
      const remaining = circumference - filled;
      return `${filled} ${remaining}`;
    });
  }
</script>

<div
  class="circle-container"
  style="{`--scale: ${buttonClicked ? '1' : '0.2'}; --top: ${
    buttonClicked ? '50%' : '1%'
  }; --left: ${buttonClicked ? '50%' : '0.5%'}; --translate: ${
    buttonClicked ? '-50%' : '0'
  };`}}"
>
  <svg width="140" height="140" viewBox="0 0 140 140">
    <!-- Larger White Inner Circle to Extend Beyond Progress Circle -->
    <circle class="inner-circle" cx="70" cy="70" r={radius + 10} />

    <!-- Progress Segments -->
    {#each segmentColors as color, index}
      <!-- Unfilled part in grey -->
      <circle
        class="progress-circle unfilled"
        cx="70"
        cy="70"
        r={radius}
        stroke-dasharray={`${circumference / 5} ${
          circumference - circumference / 5
        }`}
        stroke-dashoffset={-index * (circumference / 5)}
      />

      <!-- Filled part in segment color -->
      <circle
        class="progress-circle filled"
        cx="70"
        cy="70"
        r={radius}
        stroke={color}
        stroke-dasharray={dashArrays[index]}
        stroke-dashoffset={-index * (circumference / 5)}
      />
    {/each}
  </svg>
</div>

<style>
  .circle-container {
    position: absolute;
    width: 140px;
    height: 140px;
    top: var(--top);
    left: var(--left);
    z-index: 100;
    transform: scale(var(--scale)) translate(var(--translate), var(--translate));
    transform-origin: top left;
  }
  svg {
    transform: rotate(-90deg);
  }
  .inner-circle {
    fill: white; /* White fill for the extended inner circle */
    stroke: white; /* Optional stroke to ensure clean edges */
    stroke-width: 10; /* Stroke width adds 10px outside and inside */
  }
  .progress-circle {
    fill: none;
    stroke-width: 13;
    transition: stroke-dasharray 0.3s ease;
  }
  .progress-circle.unfilled {
    stroke: hsla(206, 41%, 93%, 1);
  }
  .progress-circle.filled {
    position: absolute; /* Draw filled circle above unfilled */
  }
</style>
